import React, { FunctionComponent, memo } from 'react'

import { LmiProAddon } from '@lmipro/state/add-on'
import { ADDONS } from '@lmipro/constants'
import { getAddonPrice } from '@lmipro/utils'
import { ConnectedLmiProFormatCurrency } from '@lmipro/connectedComponents'
import { Tooltip } from '@common/components'
// Import Local Styles
import { LmiProCouponProcessedDataPerSku } from '@lmipro/state/coupon/state'
import { isEmpty } from 'lodash-es'
import styles from '../../connectedComponents/OrderSummary/order-summary.module.css'

export interface LmiProOrderSummaryAddonsProps {
    addon: LmiProAddon
    showSubtotal?: boolean
    isAnnual: boolean
    currency: string
    content: {
        year: string
        month: string
        months: string
        percentageOffPromoDiscount: string
        percentageOffVolumeDiscount: string
        prorateDiscount: string
        tooltips: {
            [key: string]: string
        }
    }
    couponProcessedDataPerSku: LmiProCouponProcessedDataPerSku | null
    isCouponValidAndApplied: boolean
    isPercentageCoupon: boolean
    currentTier: string
    quantity?: number
    title?: string
    prorateDiscount?: number
    subLabels?: string[]
}

export const LmiProOrderSummaryAddons: FunctionComponent<LmiProOrderSummaryAddonsProps> = ({
    addon,
    showSubtotal = true,
    isAnnual,
    currency,
    content,
    couponProcessedDataPerSku,
    isCouponValidAndApplied,
    isPercentageCoupon,
    currentTier,
    quantity,
    title,
    subLabels = [],
    prorateDiscount,
}) => {
    if (!addon) return null
    const addonCouponData = couponProcessedDataPerSku ? couponProcessedDataPerSku[addon.sKU] : null
    const isCouponAppliedtoAddon =
        isCouponValidAndApplied && couponProcessedDataPerSku && addonCouponData && !isEmpty(addonCouponData)
    const addonCouponDiscountPercentage = addonCouponData?.discountPercentage || 0
    const addonCouponDiscountAmount = addonCouponData?.discountTotalSavings || 0
    const discountLineClasses = `${styles.small} ${styles.teal} ${styles['sub-section']}`
    const addonPrice = getAddonPrice(addon, isAnnual, currency, currentTier)

    return (
        <div key={addon.key} data-qat={`addon-${addon.sKU}-summary`}>
            <div className={styles.row}>
                <span className={`${styles['item-sub-title']} ${styles.total}`} data-qat="line-item-name">
                    {title}
                </span>
                {showSubtotal && (
                    <span className={`${styles.item}`} data-qat="line-item-subtotal">
                        <ConnectedLmiProFormatCurrency value={quantity ? addonPrice * quantity : addonPrice} />
                    </span>
                )}
            </div>

            <div className={styles.row}>
                <span className={styles.small} data-qat="line-item-sub-labels">
                    {subLabels.map((label) => (
                        <span key={label}>
                            {label.split(/ /).map((word) =>
                                word === ADDONS.ADDON_PRICE_REPLACE ? (
                                    <em key={label + addonPrice}>
                                        <ConnectedLmiProFormatCurrency value={addonPrice} />{' '}
                                    </em>
                                ) : (
                                    `${word} `
                                )
                            )}
                            <br />
                        </span>
                    ))}
                </span>
            </div>

            {isCouponAppliedtoAddon && (
                <div className={styles.row}>
                    <span className={discountLineClasses}>
                        <span data-qat="coupon-discount-percentage">
                            {isPercentageCoupon ? (
                                `${addonCouponDiscountPercentage}%`
                            ) : (
                                <ConnectedLmiProFormatCurrency value={addonCouponDiscountAmount} />
                            )}
                            &nbsp;
                        </span>
                        {content.percentageOffVolumeDiscount}
                    </span>
                    <span className={discountLineClasses} data-qat="coupon-discount-price">
                        -
                        <ConnectedLmiProFormatCurrency value={addonCouponDiscountAmount} />
                    </span>
                </div>
            )}

            {!!prorateDiscount && (
                <div className={styles.row}>
                    <span className={`${styles.small} ${styles.teal}`}>
                        <span data-qat="prorate-discount-percentage">{content.prorateDiscount}&nbsp;</span>
                        <Tooltip
                            content={content.tooltips.proratedadjustmentTooltip}
                            additionalClass={styles['order-tooltip']}
                            contentClass={styles['order-tooltip-content']}
                        />
                    </span>

                    <span className={`${styles.small} ${styles.teal}`} data-qat="volume-discount-price">
                        <ConnectedLmiProFormatCurrency value={prorateDiscount} />
                    </span>
                </div>
            )}
        </div>
    )
}

export const LmiProMemoOrderSummaryAddon = memo(LmiProOrderSummaryAddons)
